/** 在 HTML DOM 中按纯文本定位/替换（支持跨标签、空白归一化） */

import { findExcelCellElement, parseExcelCellRef } from '@/utils/excelDocumentHtml';

const SKIP_SELECTOR = '.comment-highlight, script, style';
const SKIP_SELECTOR_NO_COMMENT = 'script, style';

export function normalizeMatchText(text: string): string {
  return (text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

export function buildNeedleCandidates(...needles: Array<string | null | undefined>): string[] {
  const items: string[] = [];
  for (const raw of needles) {
    const s = String(raw ?? '').trim();
    if (!s) continue;
    items.push(s);
    const norm = normalizeMatchText(s);
    if (norm && norm !== s) items.push(norm);
    if (/[\r\n\u2028\u2029]/.test(s)) {
      const flattened = normalizeMatchText(s.replace(/[\r\n\u2028\u2029]+/g, ' '));
      if (flattened && !items.includes(flattened)) items.push(flattened);
      const segments = s.split(/[\r\n\u2028\u2029]+/).map((part) => normalizeMatchText(part)).filter(Boolean);
      if (segments.length > 1) {
        const joined = segments.join('');
        if (joined.length >= 4 && !items.includes(joined)) items.push(joined);
      }
    }
    const compact = s.replace(/\s+/g, '');
    if (compact.length >= 4 && compact !== s && !items.includes(compact)) items.push(compact);
  }
  return [...new Set(items)];
}

/** 多行/跨段引用时不应限制在单个 paragraphIndex 内 */
export function isMultilineQuoteText(text: string): boolean {
  return /[\r\n\u2028\u2029]/.test(String(text ?? ''));
}

export function isCrossBlockQuoteLabel(paragraphLabel?: string | null): boolean {
  const label = String(paragraphLabel ?? '').trim();
  return label === '跨段选中' || /跨段|多段|跨段落/.test(label);
}

/** 跨段或多行引用应全文检索，不能 scoped 到首段 */
export function isCrossBlockQuote(
  quoted: string,
  paragraphLabel?: string | null,
): boolean {
  return isMultilineQuoteText(quoted) || isCrossBlockQuoteLabel(paragraphLabel);
}

/** 从选区提取与 DOM 归一化索引一致的引用原文（跨段时用换行连接各段） */
export function extractQuoteTextFromRange(range: Range, root: HTMLElement): string {
  const blocks = collectAnchorBlocksInRange(range, root);
  if (blocks.length <= 1) {
    let chunk = '';
    for (const node of collectTextNodes(root, [], { includeCommentHighlights: true })) {
      if (!range.intersectsNode(node)) continue;
      let start = 0;
      let end = node.length;
      if (node === range.startContainer) start = range.startOffset;
      if (node === range.endContainer) end = range.endOffset;
      if (start >= end) continue;
      chunk += (node.nodeValue || '').slice(start, end);
    }
    return chunk.replace(/\u00a0/g, ' ') || (range.toString() || '').replace(/\u00a0/g, ' ');
  }
  const parts: string[] = [];
  for (const block of blocks) {
    let chunk = '';
    for (const node of collectTextNodes(block, [], { includeCommentHighlights: true })) {
      if (!range.intersectsNode(node)) continue;
      let start = 0;
      let end = node.length;
      if (node === range.startContainer) start = range.startOffset;
      if (node === range.endContainer) end = range.endOffset;
      if (start >= end) continue;
      chunk += (node.nodeValue || '').slice(start, end);
    }
    const normalized = chunk.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (normalized) parts.push(normalized);
  }
  if (!parts.length) return (range.toString() || '').replace(/\u00a0/g, ' ');
  return parts.join('\n');
}

export function collectAnchorBlocksInRange(range: Range, root: HTMLElement): HTMLElement[] {
  const blocks = new Set<HTMLElement>();
  for (const text of collectTextNodes(root, [], { includeCommentHighlights: true })) {
    if (!range.intersectsNode(text)) continue;
    const block = text.parentElement?.closest(ANCHOR_BLOCK_SELECTOR) as HTMLElement | null;
    if (block) blocks.add(block);
  }
  return [...blocks];
}

export function getRangeStartBlock(range: Range): HTMLElement | null {
  const startEl = range.startContainer.nodeType === Node.ELEMENT_NODE
    ? (range.startContainer as Element)
    : range.startContainer.parentElement;
  return startEl?.closest(ANCHOR_BLOCK_SELECTOR) as HTMLElement | null;
}

export function getRangeEndBlock(range: Range): HTMLElement | null {
  const endEl = range.endContainer.nodeType === Node.ELEMENT_NODE
    ? (range.endContainer as Element)
    : range.endContainer.parentElement;
  return endEl?.closest(ANCHOR_BLOCK_SELECTOR) as HTMLElement | null;
}

/** 选区终点是否未覆盖段落块末尾（末行仅部分选中） */
export function isRangeEndPartialInBlock(range: Range, block: HTMLElement): boolean {
  const doc = block.ownerDocument;
  if (!doc) return false;
  const blockRange = doc.createRange();
  blockRange.selectNodeContents(block);
  try {
    return range.compareBoundaryPoints(Range.END_TO_END, blockRange) < 0;
  } catch {
    return false;
  }
}

export function isMultiBlockRange(range: Range, root: HTMLElement): boolean {
  return collectAnchorBlocksInRange(range, root).length > 1;
}

export function resolveDocumentQuoteLocateScope(
  quoted: string,
  paragraphIndex?: number | null,
  paragraphLabel?: string | null,
  fieldPath?: string | null,
): { paragraphIndex?: number | null; paragraphLabel?: string | null } {
  if (fieldPath === 'document-body' || isCrossBlockQuote(quoted, paragraphLabel)) {
    return { paragraphIndex: null, paragraphLabel: null };
  }
  return { paragraphIndex: paragraphIndex ?? null, paragraphLabel: paragraphLabel ?? null };
}

export interface TextPosition {
  node: Text;
  offset: number;
}

export function collectTextNodes(
  root: HTMLElement,
  out: Text[] = [],
  options?: { includeCommentHighlights?: boolean },
): Text[] {
  const skipSelector = options?.includeCommentHighlights ? SKIP_SELECTOR_NO_COMMENT : SKIP_SELECTOR;
  const doc = root.ownerDocument;
  if (!doc) return out;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.parentElement?.closest(skipSelector)) continue;
    out.push(node as Text);
  }
  return out;
}

export const ANCHOR_BLOCK_SELECTOR = 'p, li, h1, h2, h3, h4, h5, h6, td, th, blockquote';

const BLOCK_SELECTOR = `${ANCHOR_BLOCK_SELECTOR}, div, pre`;

export function getAnchorBlocks(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll(ANCHOR_BLOCK_SELECTOR)).filter((el) => {
    if (el.classList.contains('excel-sheet-title')) return false;
    const t = (el.textContent || '').trim();
    return !!t;
  }) as HTMLElement[];
}

function blockParent(node: Text): Element | null {
  return node.parentElement?.closest(BLOCK_SELECTOR) ?? node.parentElement;
}

/** 构建归一化全文索引（连续空白折叠为一个空格） */
export function buildNormalizedTextIndex(
  root: HTMLElement,
  options?: { includeCommentHighlights?: boolean },
): { text: string; positions: TextPosition[] } {
  const nodes = collectTextNodes(root, [], options);
  const positions: TextPosition[] = [];
  let text = '';
  let lastBlock: Element | null = null;
  for (const n of nodes) {
    const block = blockParent(n);
    if (lastBlock && block && block !== lastBlock && text.length > 0 && !text.endsWith(' ')) {
      text += ' ';
      positions.push({ node: n, offset: 0 });
    }
    lastBlock = block ?? lastBlock;
    const val = (n.nodeValue || '').replace(/\u00a0/g, ' ');
    for (let i = 0; i < val.length; i += 1) {
      const ch = val[i];
      if (/\s/.test(ch)) {
        if (text.length === 0 || text.endsWith(' ')) continue;
        text += ' ';
        positions.push({ node: n, offset: i });
      } else {
        text += ch;
        positions.push({ node: n, offset: i });
      }
    }
  }
  return { text, positions };
}

function findTextRangeViaNormalizedIndex(
  root: HTMLElement,
  normNeedle: string,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const doc = root.ownerDocument;
  if (!doc || !normNeedle) return null;
  const { text, positions } = buildNormalizedTextIndex(root, options);
  const idx = text.indexOf(normNeedle);
  if (idx < 0) return null;
  const endIdx = idx + normNeedle.length - 1;
  if (idx >= positions.length || endIdx >= positions.length) return null;
  const startPos = positions[idx];
  const endPos = positions[endIdx];
  if (!startPos || !endPos) return null;
  const range = doc.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, Math.min((endPos.node.nodeValue || '').length, endPos.offset + 1));
  return range;
}

function findTextRangeViaCompactIndex(
  root: HTMLElement,
  needle: string,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const doc = root.ownerDocument;
  if (!doc) return null;
  const compactNeedle = String(needle ?? '').replace(/\u00a0/g, '').replace(/\s+/g, '');
  if (compactNeedle.length < 2) return null;
  const nodes = collectTextNodes(root, [], options);
  let compact = '';
  const positions: TextPosition[] = [];
  for (const n of nodes) {
    const val = (n.nodeValue || '').replace(/\u00a0/g, '');
    for (let i = 0; i < val.length; i += 1) {
      if (/\s/.test(val[i])) continue;
      compact += val[i];
      positions.push({ node: n, offset: i });
    }
  }
  const idx = compact.indexOf(compactNeedle);
  if (idx < 0) return null;
  const endIdx = idx + compactNeedle.length - 1;
  if (idx >= positions.length || endIdx >= positions.length) return null;
  const startPos = positions[idx];
  const endPos = positions[endIdx];
  if (!startPos || !endPos) return null;
  const range = doc.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, Math.min((endPos.node.nodeValue || '').length, endPos.offset + 1));
  return range;
}

export function splitQuoteSegments(text: string): string[] {
  return String(text ?? '')
    .split(/[\r\n\u2028\u2029]+/)
    .map((part) => normalizeMatchText(part))
    .filter(Boolean);
}

/** 按段落/列表项逐段匹配（跨段选中时比全文 index 更稳） */
function findTextRangeByBlockSegments(
  root: HTMLElement,
  needle: string,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const segments = splitQuoteSegments(needle);
  if (segments.length < 2) return null;
  const doc = root.ownerDocument;
  if (!doc) return null;
  const blocks = getAnchorBlocks(root);
  if (blocks.length < segments.length) return null;

  for (let i = 0; i <= blocks.length - segments.length; i += 1) {
    let first: Range | null = null;
    let last: Range | null = null;
    let matched = true;
    for (let j = 0; j < segments.length; j += 1) {
      const blockRange = findTextRangeInElement(blocks[i + j], segments[j], options);
      if (!blockRange) {
        matched = false;
        break;
      }
      if (!first) first = blockRange;
      last = blockRange;
    }
    if (!matched || !first || !last) continue;
    const range = doc.createRange();
    range.setStart(first.startContainer, first.startOffset);
    range.setEnd(last.endContainer, last.endOffset);
    if (rangeMatchesNeedle(range, needle)) return range;
  }
  return null;
}

function findDirectTextRange(
  root: HTMLElement,
  needle: string,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const normNeedle = normalizeMatchText(needle);
  if (!normNeedle) return null;
  return findTextRangeViaNormalizedIndex(root, normNeedle, options);
}

function findTextRangeInElement(
  root: HTMLElement,
  needle: string,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const normNeedle = normalizeMatchText(needle);
  if (!normNeedle) return null;

  const bySegments = findTextRangeByBlockSegments(root, needle, options);
  if (bySegments) return bySegments;

  const viaIndex = findTextRangeViaNormalizedIndex(root, normNeedle, options);
  if (viaIndex && rangeMatchesNeedle(viaIndex, needle)) return viaIndex;

  const viaCompact = findTextRangeViaCompactIndex(root, needle, options);
  if (viaCompact && rangeMatchesNeedle(viaCompact, needle)) return viaCompact;

  return viaIndex ?? viaCompact;
}

function isExcelDocumentRoot(root: HTMLElement): boolean {
  return !!root.querySelector('.excel-workbook, .excel-sheet, table.excel-table');
}

function findTextRangeInExcelCellScope(
  cell: HTMLElement,
  needle: string,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const inCell = findTextRangeInElement(cell, needle, options);
  if (inCell) return inCell;
  const row = cell.parentElement;
  if (row?.tagName === 'TR') {
    const inRow = findTextRangeInElement(row as HTMLElement, needle, options);
    if (inRow) return inRow;
  }
  return null;
}

export function findTextRangeInRoot(
  root: HTMLElement,
  needle: string,
  blockIndex?: number | null,
  cellRef?: string | null,
  options?: { allowRootFallback?: boolean; includeCommentHighlights?: boolean },
): Range | null {
  const allowRootFallback = options?.allowRootFallback !== false;
  const searchOpts = { includeCommentHighlights: options?.includeCommentHighlights };
  const scopedCellRef = parseExcelCellRef(cellRef);
  if (scopedCellRef) {
    const cell = findExcelCellElement(root, scopedCellRef);
    if (cell) {
      const scoped = findTextRangeInExcelCellScope(cell, needle, searchOpts);
      if (scoped) return scoped;
    }
    return allowRootFallback ? findTextRangeInElement(root, needle, searchOpts) : null;
  }
  const excelDoc = isExcelDocumentRoot(root);
  if (!excelDoc && blockIndex != null && blockIndex > 0) {
    const blocks = getAnchorBlocks(root);
    const block = blocks[blockIndex - 1];
    if (block) {
      const scoped = findTextRangeInElement(block, needle, searchOpts);
      if (scoped) return scoped;
    }
    if (!allowRootFallback) return null;
  }
  return findTextRangeInElement(root, needle, searchOpts);
}

/** 段落/单元格 scoped 定位失败后自动回退全文；默认可搜索批注高亮内的文本 */
export function findTextRangeInRootWithFallback(
  root: HTMLElement,
  needle: string,
  blockIndex?: number | null,
  cellRef?: string | null,
  options?: { includeCommentHighlights?: boolean },
): Range | null {
  const searchOpts = { includeCommentHighlights: options?.includeCommentHighlights ?? true };
  for (const candidate of buildNeedleCandidates(needle)) {
    const scoped = findTextRangeInRoot(root, candidate, blockIndex, cellRef, {
      allowRootFallback: false,
      ...searchOpts,
    });
    if (scoped && rangeMatchesNeedleRelaxed(scoped, candidate)) return scoped;
    const global = findTextRangeInRoot(root, candidate, null, null, {
      allowRootFallback: true,
      ...searchOpts,
    });
    if (global && rangeMatchesNeedleRelaxed(global, candidate)) return global;
  }
  return null;
}

function rangeMatchesNeedleRelaxed(range: Range, needle: string): boolean {
  if (rangeMatchesNeedle(range, needle)) return true;
  const got = (range.toString() || '').replace(/\u00a0/g, '').replace(/\s+/g, '');
  const want = String(needle ?? '').replace(/\u00a0/g, '').replace(/\s+/g, '');
  if (!got || !want) return false;
  return got === want || got.includes(want) || want.includes(got);
}

export function rangeMatchesNeedle(range: Range, needle: string): boolean {
  const got = normalizeMatchText(range.toString());
  const want = normalizeMatchText(needle);
  if (!got || !want) return false;
  if (got === want || got.includes(want) || want.includes(got)) return true;
  const gotCompact = got.replace(/\s+/g, '');
  const wantCompact = want.replace(/\s+/g, '');
  if (!gotCompact || !wantCompact) return false;
  return gotCompact === wantCompact || gotCompact.includes(wantCompact) || wantCompact.includes(gotCompact);
}

/** 在滚动容器内测量锚点文本的 contentOffsetY（与正文高亮一致） */
export function measureAnchorOffsetY(
  root: HTMLElement,
  anchorText: string,
  blockIndex?: number | null,
  cellRef?: string | null,
  paragraphLabel?: string | null,
): number | null {
  const text = (anchorText || '').trim();
  if (!text) return null;
  const scope = resolveDocumentQuoteLocateScope(text, blockIndex, paragraphLabel, 'document-body');
  const resolvedCellRef = cellRef ?? parseExcelCellRef(scope.paragraphLabel);
  for (const candidate of buildNeedleCandidates(text)) {
    const range = findTextRangeInRootWithFallback(root, candidate, scope.paragraphIndex, resolvedCellRef);
    if (!range) continue;
    const start = range.cloneRange();
    start.collapse(true);
    const rect = start.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    if (!rect.height && !rect.width) {
      const fullRect = range.getBoundingClientRect();
      if (!fullRect.height && !fullRect.width) continue;
      return fullRect.top - rootRect.top + root.scrollTop;
    }
    return rect.top - rootRect.top + root.scrollTop;
  }
  return null;
}

export function measureHighlightOffsetY(root: HTMLElement, el: HTMLElement): number {
  const rootRect = root.getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  return rect.top - rootRect.top + root.scrollTop;
}

export function wrapRangeWithHighlight(range: Range, commentId: string): boolean {
  const doc = range.startContainer.ownerDocument;
  if (!doc) return false;
  const span = doc.createElement('span');
  span.className = 'comment-highlight';
  span.setAttribute('data-comment-id', commentId);
  try {
    range.surroundContents(span);
    return true;
  } catch {
    const fragment = range.extractContents();
    span.appendChild(fragment);
    range.insertNode(span);
    return true;
  }
}

export function findAndHighlightInRoot(
  root: HTMLElement,
  needle: string,
  commentId: string,
  blockIndex?: number | null,
  cellRef?: string | null,
): boolean {
  const range = findTextRangeInRoot(root, needle, blockIndex, cellRef);
  if (!range) return false;
  return wrapRangeWithHighlight(range, commentId);
}

export function clearTransientHighlights(root: HTMLElement) {
  root.querySelectorAll('.comment-highlight--transient').forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
}

export function wrapRangeAsTransientHighlight(range: Range): HTMLElement | null {
  const first = wrapRangeWithHighlightClass(range, 'comment-highlight comment-highlight--transient', 'span');
  if (first) first.setAttribute('data-comment-id', '__transient__');
  return first;
}

function rangeSpansMultipleBlocks(range: Range): boolean {
  const startEl = range.startContainer.nodeType === Node.ELEMENT_NODE
    ? (range.startContainer as Element)
    : range.startContainer.parentElement;
  const endEl = range.endContainer.nodeType === Node.ELEMENT_NODE
    ? (range.endContainer as Element)
    : range.endContainer.parentElement;
  const startBlock = startEl?.closest?.(ANCHOR_BLOCK_SELECTOR) ?? null;
  const endBlock = endEl?.closest?.(ANCHOR_BLOCK_SELECTOR) ?? null;
  return !!(startBlock && endBlock && startBlock !== endBlock);
}

function fragmentContainsBlockElement(fragment: DocumentFragment): boolean {
  return !!fragment.querySelector(BLOCK_SELECTOR);
}

function wrapRangeWithClass(range: Range, className: string, tagName: 'mark' | 'span'): HTMLElement | null {
  const doc = range.startContainer.ownerDocument;
  if (!doc) return null;
  const el = doc.createElement(tagName);
  el.className = className;
  try {
    range.surroundContents(el);
    return el;
  } catch {
    if (rangeSpansMultipleBlocks(range)) return null;
    const probe = range.cloneContents();
    if (fragmentContainsBlockElement(probe)) return null;
    if (range.startContainer === range.endContainer && range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text;
      const seg = doc.createRange();
      seg.setStart(textNode, range.startOffset);
      seg.setEnd(textNode, range.endOffset);
      try {
        seg.surroundContents(el);
        return el;
      } catch {
        return null;
      }
    }
    const fragment = range.extractContents();
    if (fragmentContainsBlockElement(fragment)) return null;
    el.appendChild(fragment);
    range.insertNode(el);
    return el;
  }
}

function wrapRangeSegmentsWithHighlightClass(
  range: Range,
  className: string,
  tagName: 'mark' | 'span',
): HTMLElement | null {
  const doc = range.startContainer.ownerDocument;
  if (!doc) return null;
  const ancestor = range.commonAncestorContainer;
  const root = ancestor.nodeType === Node.ELEMENT_NODE
    ? (ancestor as HTMLElement)
    : ancestor.parentElement;
  if (!root) return null;

  let first: HTMLElement | null = null;
  for (const text of collectTextNodes(root, [], { includeCommentHighlights: true })) {
    if (!range.intersectsNode(text)) continue;
    let start = 0;
    let end = text.length;
    if (text === range.startContainer) start = range.startOffset;
    if (text === range.endContainer) end = range.endOffset;
    if (start >= end) continue;
    const seg = doc.createRange();
    seg.setStart(text, start);
    seg.setEnd(text, end);
    const part = wrapRangeWithClass(seg, className, tagName);
    if (part && !first) first = part;
  }
  return first;
}

/** 跨段落/跨标签安全高亮：优先整体包裹，失败则按文本节点分段包裹 */
export function wrapRangeWithHighlightClass(range: Range, className: string, tagName: 'mark' | 'span' = 'mark'): HTMLElement | null {
  const doc = range.startContainer.ownerDocument;
  if (!doc) return null;

  if (!rangeSpansMultipleBlocks(range)) {
    const direct = wrapRangeWithClass(range, className, tagName);
    if (direct) return direct;
  }

  return wrapRangeSegmentsWithHighlightClass(range, className, tagName);
}

export function replaceTextInHtmlRoot(root: HTMLElement, anchor: string, replacement: string): boolean {
  const range = findTextRangeInRoot(root, anchor);
  if (!range) return false;
  range.deleteContents();
  range.insertNode(root.ownerDocument!.createTextNode(replacement ?? ''));
  return true;
}

function parseHtmlRoot(html: string): HTMLElement | null {
  try {
    const doc = new DOMParser().parseFromString(`<div id="aipd-text-root">${html}</div>`, 'text/html');
    return doc.getElementById('aipd-text-root');
  } catch {
    return null;
  }
}

export function replaceTextInHtml(html: string, anchor: string, replacement: string): { html: string; replaced: boolean } {
  const root = parseHtmlRoot(html);
  if (!root) return { html, replaced: false };
  const ok = replaceTextInHtmlRoot(root, anchor, replacement);
  return { html: ok ? root.innerHTML : html, replaced: ok };
}

/** 在指定段落/单元格范围内替换文本（与定位逻辑一致） */
export function replaceTextInHtmlScoped(
  html: string,
  anchor: string,
  replacement: string,
  blockIndex?: number | null,
  cellRef?: string | null,
): { html: string; replaced: boolean } {
  const root = parseHtmlRoot(html);
  if (!root) return { html, replaced: false };
  const range = findTextRangeInRoot(root, anchor, blockIndex, cellRef);
  if (!range) return { html, replaced: false };
  range.deleteContents();
  range.insertNode(root.ownerDocument!.createTextNode(replacement ?? ''));
  return { html: root.innerHTML, replaced: true };
}

export function applyTextReplacementsInHtml(
  html: string,
  items: Array<{ anchor_text: string; suggested_replacement: string }>,
): { html: string; appliedCount: number } {
  const root = parseHtmlRoot(html);
  if (!root) return { html, appliedCount: 0 };
  const sorted = [...items]
    .filter((f) => (f.anchor_text || '').trim())
    .sort((a, b) => (b.anchor_text?.length || 0) - (a.anchor_text?.length || 0));
  let appliedCount = 0;
  for (const f of sorted) {
    if (replaceTextInHtmlRoot(root, f.anchor_text.trim(), f.suggested_replacement ?? '')) {
      appliedCount += 1;
    }
  }
  return { html: root.innerHTML, appliedCount };
}

function shrinkRangeToAnchorStart(range: Range, maxLen: number): Range {
  const doc = range.startContainer.ownerDocument;
  if (!doc || maxLen <= 0) return range;
  const next = doc.createRange();
  next.setStart(range.startContainer, range.startOffset);
  if (range.startContainer.nodeType === Node.TEXT_NODE) {
    const text = range.startContainer as Text;
    next.setEnd(text, Math.min(text.length, range.startOffset + maxLen));
    return next;
  }
  next.collapse(true);
  return next;
}

/** 评论锚点失效时，定位到原引用起始位置（首段/首句/段落索引） */
export function findCommentAnchorStartRange(
  root: HTMLElement,
  anchorText: string,
  paragraphIndex?: number | null,
  paragraphLabel?: string | null,
): Range | null {
  const needle = String(anchorText ?? '').trim();
  const doc = root.ownerDocument;
  if (!doc) return null;
  const scope = resolveDocumentQuoteLocateScope(needle, paragraphIndex, paragraphLabel);
  const cellRef = parseExcelCellRef(scope.paragraphLabel);

  if (needle) {
    for (const candidate of buildNeedleCandidates(needle)) {
      const range = findTextRangeInRootWithFallback(root, candidate, scope.paragraphIndex, cellRef);
      if (range) return shrinkRangeToAnchorStart(range, Math.min(20, candidate.length));
    }
    const segments = splitQuoteSegments(needle);
    const firstSeg = segments[0];
    if (firstSeg && firstSeg !== normalizeMatchText(needle)) {
      for (const candidate of buildNeedleCandidates(firstSeg)) {
        const range = findTextRangeInRootWithFallback(root, candidate, scope.paragraphIndex, cellRef);
        if (range) return shrinkRangeToAnchorStart(range, Math.min(20, candidate.length));
      }
    }
  }

  if (scope.paragraphIndex && scope.paragraphIndex > 0) {
    const blocks = getAnchorBlocks(root);
    const block = blocks[scope.paragraphIndex - 1];
    if (block) {
      const range = doc.createRange();
      range.selectNodeContents(block);
      range.collapse(true);
      return range;
    }
  }

  if (cellRef) {
    const cell = findExcelCellElement(root, cellRef);
    if (cell) {
      const range = doc.createRange();
      range.selectNodeContents(cell);
      range.collapse(true);
      return range;
    }
  }

  return null;
}

export function isCommentAnchorLocatableInRoot(
  root: HTMLElement,
  anchorText: string,
  paragraphIndex?: number | null,
  paragraphLabel?: string | null,
): boolean {
  const needle = String(anchorText ?? '').trim();
  if (!needle) return false;
  const scope = resolveDocumentQuoteLocateScope(needle, paragraphIndex, paragraphLabel);
  const cellRef = parseExcelCellRef(scope.paragraphLabel);
  for (const candidate of buildNeedleCandidates(needle)) {
    const range = findTextRangeInRootWithFallback(root, candidate, scope.paragraphIndex, cellRef);
    if (range && rangeMatchesNeedle(range, candidate)) return true;
  }
  return false;
}
